import { useState } from "react";
import { AdminUsers, Invites } from "../../lib/apiTyped";
import { useResource } from "../../lib/useResource";
import { useApiCall } from "../../lib/useToastError";
import { useNotifications } from "../../components/shared/NotificationsProvider";
import { PageHeader } from "../../components/AppLayout";
import { Card } from "../../components/shared/Card";
import { DataTable, type Column } from "../../components/shared/DataTable";
import { Button } from "../../components/shared/Button";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { Modal } from "../../components/shared/Modal";
import { FormInput } from "../../components/shared/FormInput";
import {
  UserActionsMenu,
  type AdminUserRow,
} from "../../components/admin/UserActionsMenu";
import { PromoteToOwnerModal } from "../../components/admin/PromoteToOwnerModal";
import { TransferOwnershipModal } from "../../components/admin/TransferOwnershipModal";
import { fmtDate } from "../../lib/format";

type UserRow = AdminUserRow & Record<string, unknown>;

export function AdminUsersPage() {
  const users = useResource(() => AdminUsers.list({ pageSize: 100 }), []);
  const invites = useResource(() => Invites.list(), []);
  const [openInvite, setOpenInvite] = useState(false);
  const [promote, setPromote] = useState<AdminUserRow | null>(null);
  const [transfer, setTransfer] = useState<AdminUserRow | null>(null);
  const { run } = useApiCall();
  const { confirm } = useNotifications();

  async function deleteUser(u: AdminUserRow) {
    const ok = await confirm({
      title: "Delete user?",
      message: `${u.email ?? u.uid} will lose all access. Their Firestore profile and Auth account are removed.`,
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    await run(() => AdminUsers.delete(u.uid), { successMessage: "User deleted." });
    await users.refetch();
  }
  async function revokeInvite(email: string) {
    await run(() => Invites.revoke(email), { successMessage: "Invite revoked." });
    await invites.refetch();
  }

  const userCols: Column<UserRow>[] = [
    { header: "Email", render: (r) => r.email ?? "—" },
    {
      header: "Account",
      render: (r) => (r.accountType as string | undefined) ?? "—",
    },
    {
      header: "User ID",
      render: (r) => <IdCode value={r.uid} />,
    },
    {
      header: "Org",
      render: (r) =>
        r.defaultOrgId ? (
          <IdCode value={String(r.defaultOrgId)} />
        ) : (
          <StatusBadge status="invited" />
        ),
    },
    { header: "Created", render: (r) => fmtDate(r.createdAt) },
  ];

  return (
    <>
      <PageHeader
        title="Users"
        action={
          <Button variant="primary" onClick={() => setOpenInvite(true)}>
            + Invite client
          </Button>
        }
      />

      {invites.data && invites.data.invites.length > 0 && (
        <Card className="p-5 mb-6">
          <div className="text-xs font-semibold uppercase tracking-wider text-ink-3 mb-3">
            Pending invites
          </div>
          <ul className="divide-y divide-border">
            {invites.data.invites.map((inv) => (
              <li key={inv.id} className="py-3 flex items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-ink">{inv.email}</span>
                    {inv.designPartner === true && (
                      <span
                        className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-ink"
                        style={{
                          backgroundColor: "#F5EDE0",
                          border: "1px solid rgba(200,169,81,0.35)",
                        }}
                      >
                        Design partner
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-ink-3">
                    {inv.kind === "member" ? "member" : "org owner"} · {inv.orgName ?? inv.orgId ?? ""}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status="pending" />
                  <Button variant="ghost" size="sm" onClick={() => revokeInvite(inv.email)}>
                    Revoke
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <DataTable
        rows={(users.data?.users ?? null) as UserRow[] | null}
        loading={users.loading}
        rowKey={(r) => r.uid}
        columns={userCols}
        rowActions={(r) => (
          <UserActionsMenu
            user={r}
            onPromote={() => setPromote(r)}
            onTransfer={() => setTransfer(r)}
            onDelete={() => deleteUser(r)}
          />
        )}
        empty={{ title: "No users yet" }}
      />

      {openInvite && (
        <InviteClientModal
          onClose={() => setOpenInvite(false)}
          onInvited={() => {
            setOpenInvite(false);
            void invites.refetch();
          }}
        />
      )}
      {promote && (
        <PromoteToOwnerModal
          user={promote}
          onClose={() => setPromote(null)}
          onDone={() => {
            setPromote(null);
            void users.refetch();
          }}
        />
      )}
      {transfer && (
        <TransferOwnershipModal
          user={transfer}
          onClose={() => setTransfer(null)}
          onDone={() => {
            setTransfer(null);
            void users.refetch();
          }}
        />
      )}
    </>
  );
}

// Compact identifier display: shows the last 8 chars of long Firebase /
// org IDs (the meaningful tail) and copies the full value to clipboard on
// click. Keeps the table dense without losing the ability to inspect the
// full id when needed.
function IdCode({ value }: { value: string }) {
  const tail = value.length > 12 ? `…${value.slice(-8)}` : value;
  return (
    <button
      type="button"
      onClick={() => {
        try {
          void navigator.clipboard.writeText(value);
        } catch {
          /* ignore */
        }
      }}
      title={`${value}\n(click para copiar)`}
      className="font-mono text-xs text-ink-3 hover:text-ink transition-colors"
    >
      {tail}
    </button>
  );
}

function InviteClientModal({
  onClose,
  onInvited,
}: {
  onClose: () => void;
  onInvited: () => void;
}) {
  const [email, setEmail] = useState("");
  const [orgName, setOrgName] = useState("");
  const [designPartner, setDesignPartner] = useState(false);
  const [busy, setBusy] = useState(false);
  const { run } = useApiCall();

  async function submit() {
    if (busy || !email.trim() || !orgName.trim()) return;
    setBusy(true);
    const result = await run(
      () =>
        Invites.create({
          email: email.trim(),
          orgName: orgName.trim(),
          designPartner,
        }),
      { successMessage: "Invite created." },
    );
    setBusy(false);
    if (result) onInvited();
  }

  return (
    <Modal onClose={onClose} maxWidth="28rem">
      <h3 className="text-lg font-black mb-1 pr-6 text-ink">Invite client</h3>
      <p className="text-sm text-ink-3 mb-5">
        Creates a pending invite. The client signs in with Google using this
        email — that triggers automatic provisioning of their enterprise org
        with them as owner. You assign their plan and seats from the org
        detail afterwards.
      </p>
      <div className="space-y-3">
        <FormInput label="Email" value={email} onChange={setEmail} type="email" />
        <FormInput label="Organization name" value={orgName} onChange={setOrgName} />
        <label className="flex items-start gap-3 cursor-pointer pt-1">
          <input
            type="checkbox"
            checked={designPartner}
            onChange={(e) => setDesignPartner(e.target.checked)}
            className="mt-1 w-4 h-4 accent-ink shrink-0"
          />
          <div>
            <div className="text-sm font-semibold text-ink">Design partner</div>
            <div className="text-xs text-ink-3">
              The invitee will see a special welcome screen during onboarding
              with the design-partner perks.
            </div>
          </div>
        </label>
      </div>
      <div className="mt-6 flex gap-3">
        <Button variant="primary" onClick={submit} disabled={busy} fullWidth>
          {busy ? "Creating…" : "Create invite"}
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}
